/**
 * DayPlanner.js
 *
 * Copyright (c) 2026 Eric Seigne <eric.seigne@cap-rel.fr>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import { DivePlanner } from './DivePlanner.js'
import { CCR } from './CCR.js'
import { PlanError, PLAN_ERROR_CODES, PLAN_WARNING_CODES } from './errors.js'
import { debugPlanner } from '../utils/debug.js'

// The mix of the oxygen finish, hard coded rather than picked from the diluent
// list: a rebreather always carries a cylinder of pure oxygen - that is what
// feeds the loop - so there is nothing for the diver to declare. Asking him to
// describe oxygen as a "diluent" would be asking him to write down the one gas
// no rebreather ever dilutes with.
const O2_FLUSH_MIX = Object.freeze({ name: 'O2', fO2: 1, fHe: 0, fN2: 0 })

// A day of diving: several dives sharing one gas set, one environment and one
// set of planner settings, each starting from the tissues the previous one left.
//
// This module is the single bridge between the application state and the
// engine. Everything here used to live inside PlanView.computePlan, mixed with
// the DOM: a series could not be computed without a screen, and the rules that
// turn a stored dive into engine arguments (descent time convention, diluent
// resolution, setpoint override) had no test of their own. PlanView now calls
// the very same functions, so the plan of a dive is identical whether it is
// computed alone on the plan screen or as part of a day.

// One dive of the day, as the store holds it:
//   segments               the dive itself
//   ccr                    rebreather configuration, or null / disabled for OC
//   surfaceIntervalBefore  minutes at the surface since the previous dive,
//                          null on the first dive of the day
//   results                { ccr, bailout } once computed, null when pending
//
// The open circuit plan is carried in the `bailout` slot: that is the
// convention of state.results and of every screen and exporter reading it (in
// open circuit there is nothing to bail out from, the slot holds the dive).

// Create a planner from the shared settings of the day.
// The environment is part of the plan, not a display detail: without it every
// dive would be computed at sea level in salt water while the settings offer
// altitudes up to 4000 m and fresh water.
export function createPlanner(settings, environment) {
  return new DivePlanner({
    environment,
    algorithm: settings.algorithm,
    ascentRate: settings.ascentRate,
    descentRate: settings.descentRate,
    lastStopDepth: settings.lastStopDepth,
    safetyStopTime: settings.safetyStopTime,
    lastStopAscentRate: settings.lastStopAscentRate,
    decoAscentRate: settings.decoAscentRate,
    ppO2Max: settings.ppO2Max,
    ppO2Deco: settings.ppO2Deco,
    gasSwitchAtStop: settings.gasSwitchAtStop,
    gasSwitchDuration: settings.gasSwitchDuration
  })
}

// Adjust segment times when the descent time is included in the bottom time
// (settings.descentTimeIncluded, the Duration column of Subsurface).
//
// A segment shorter than its own descent is refused, not clamped. The time
// used to be cut to 0 min without a log: 60 m for 2 min at 20 m/min was
// planned as a bounce to 60 m, and nothing on screen said the diver had asked
// for something else. A time exactly equal to the descent is a legitimate
// bounce and goes through as 0 min at the bottom.
export function adjustSegmentsForDescent(segments, settings, context = 'adjustSegmentsForDescent') {
  if (!settings.descentTimeIncluded) return segments
  let previousDepth = 0
  return segments.map((seg, index) => {
    if (seg.depth > previousDepth) {
      const descentTime = (seg.depth - previousDepth) / settings.descentRate
      if (seg.time < descentTime) {
        console.error('%s: segment #%d (%om) lasts %o min, shorter than its %o min descent at %o m/min',
          context, index, seg.depth, seg.time, descentTime, settings.descentRate)
        throw new PlanError(PLAN_ERROR_CODES.TIME_SHORTER_THAN_DESCENT, {
          context,
          index: index + 1,
          depth: seg.depth,
          time: seg.time,
          descentTime: Math.round(descentTime * 10) / 10,
          descentRate: settings.descentRate
        })
      }
      previousDepth = seg.depth
      return { ...seg, time: seg.time - descentTime }
    }
    previousDepth = seg.depth
    return seg
  })
}

// Open circuit segments with the gas the diver picked for each of them, in the
// form the engine reads: `gasIndex`, the rank of that gas in the list handed
// to planOC (the enabled gases, in store order).
//
// The plan screen stores `gasId` on every segment and the engine only ever
// read `gasIndex`, which nothing wrote: the pick was printed on the segment
// card and on the sheet, and the plan below was computed on Gas.bestGas, the
// richest mix reaching the depth. With air and a trimix both enabled, a 55 m
// segment set to the trimix was planned on air, END 55 m, and nothing said so.
//
// A segment without gasId keeps the automatic choice (older saved plans, the
// scenario runners). A gasId pointing at a gas that is disabled or gone is
// refused: planning that segment on another mix is exactly the substitution
// this exists to end.
//
// `allGases` only serves the message: a disabled gas is still in the store and
// the diver has to be told which one, by name.
export function resolveSegmentGases(segments, enabledGases, context = 'resolveSegmentGases', allGases = enabledGases) {
  return segments.map((seg, index) => {
    if (seg.gasId === undefined || seg.gasId === null) return seg
    const gasIndex = enabledGases.findIndex(g => g.id === seg.gasId)
    if (gasIndex < 0) {
      const named = allGases.find(g => g.id === seg.gasId)
      console.error('%s: segment #%d (%om) is planned on gas id %o (%s), which is disabled or no longer exists among the %d enabled gases',
        context, index, seg.depth, seg.gasId, named ? named.name : 'unknown', enabledGases.length)
      throw new PlanError(PLAN_ERROR_CODES.SEGMENT_GAS_UNAVAILABLE, {
        context,
        index: index + 1,
        depth: seg.depth,
        gasId: seg.gasId,
        gas: named?.name ?? `#${seg.gasId}`
      })
    }
    return { ...seg, gasIndex }
  })
}

// Loop flushes of a rebreather dive, resolved from the diluent list.
//
// The stored shape is { depth, diluentIndex, time, keepMix }: the diver picks the mix in
// the same list the diluents come from, because that is physically what it is -
// a gas pushed into the loop through the ADV or the manual add. A flush that
// points at a diluent which no longer exists is refused rather than silently
// replaced: the whole point of a flush is the mix it puts in the loop.
//
// Depth, duration and ordering are validated by the engine
// (DivePlanner.normalizeFlushes), which is where the dive profile is known.
//
// The list is only applied when the diver has the feature switched on. Reading
// it regardless would leave a plan silently flushing after the switch was
// turned off - the editor gone from the screen, the flushes still in the
// ascent, and nothing on the sheet to explain the mix.
// Returns { flushes, warnings }: the warnings are the ones raised while
// resolving the settings, and planCCR pushes them into the plan as its own.
function resolveFlushes(ccr, diluents, mix, settings, segments) {
  const handWritten = settings?.ccrFlushEnabled === true ? (ccr?.flushes ?? []) : []
  const list = Array.isArray(handWritten) ? handWritten : []
  if (!Array.isArray(handWritten)) {
    console.error('buildCCRConfig: ccr.flushes is not a list (%o), no hand written flush applied', handWritten)
  }

  const flushes = list.map((flush, index) => {
    const source = diluents[flush?.diluentIndex]
    if (!source) {
      console.error('buildCCRConfig: flush #%d references diluent #%o, which does not exist among the %d configured',
        index, flush?.diluentIndex, diluents.length)
      throw new PlanError(PLAN_ERROR_CODES.NO_DILUENT, {
        context: 'buildCCRConfig', index, diluentIndex: flush?.diluentIndex
      })
    }
    // keepMix is passed as stored, strict true or nothing: the engine is the one
    // place that decides what a missing or malformed value means, so a plan
    // saved before this option existed reads the same here and there.
    return {
      depth: flush.depth,
      time: flush.time,
      keepMix: flush.keepMix,
      diluent: mix(source)
    }
  })

  const { flush: o2Flush, warning } = resolveO2Flush(settings, segments, flushes)
  if (o2Flush) flushes.push(o2Flush)
  return { flushes, warnings: warning ? [warning] : [] }
}

// Finishing the dive on 100 % oxygen, described once in the CCR settings rather
// than retyped on every dive.
//
// It is the standard rebreather ending: at 6 m the loop is replaced with pure
// oxygen - 1.62 bar in sea water at sea level, which is what Gas.MOD_TOLERANCE
// calls "the 6 m gas" - and the diver finishes his decompression on it. Hence
// keepMix: true, and not the usual return to the diluent in service. Once the
// loop holds oxygen the solenoid has nothing left to inject and no way to bring
// a ppO2 back down; reverting to the diluent would recompute every stop above
// the flush on nitrogen and owe the diver less decompression than he really
// does.
//
// The mix is O2_FLUSH_MIX and comes from no list: a rebreather is dived on two
// cylinders, a diluent and pure oxygen, so the oxygen is a given.
//
// Not applied silently in two cases, each one telling the diver why:
//   - a hand written flush already sits at that depth. His own line wins, and
//     the setting steps aside: normalizeFlushes refuses two flushes at the same
//     depth, and a global setting must not refuse a plan he typed himself.
//   - the dive never goes below the flush depth, so no ascent passes through it.
//
// An unusable depth or duration is a different matter and does throw: the
// settings screen cannot produce one, so it means a hand edited storage, and
// the engine refuses every other unusable input the same way.
//
// What the setting amounts to on this dive, without deciding anything about it.
// Exported because the plan screen has to show the very same verdict, read only,
// under the flush editor: a row appears in the computed ascent and nothing else
// on that screen would say where it comes from. Written once here so the screen
// cannot promise a flush the engine drops, nor stay silent on one it applies.
//
// Returns { active, depth, time, mix, reason }:
//   active  whether the flush is part of the ascent
//   mix     the oxygen it is made with, always the same literal
//   reason  null when active, otherwise 'off', 'setting', 'clash' or 'too_deep'
export function describeO2Flush(settings = {}, segments = [], plannedDepths = []) {
  const depth = settings.ccrO2FlushDepth
  const time = settings.ccrO2FlushTime
  const base = { active: false, depth, time, mix: O2_FLUSH_MIX }

  if (settings.ccrO2FlushEnabled !== true) return { ...base, reason: 'off' }

  if (!Number.isFinite(depth) || depth <= 0 || !Number.isFinite(time) || time <= 0) {
    return { ...base, reason: 'setting' }
  }

  if (plannedDepths.includes(depth)) return { ...base, reason: 'clash' }

  // An empty segment list is not a shallow dive, it is a caller that knows
  // nothing of the profile - the settings screen, which describes the flush
  // without having a dive in front of it. Only a real profile can say the
  // ascent never passes through the flush.
  const maxDepth = Math.max(0, ...segments.map(s => s?.depth ?? 0))
  if (segments.length > 0 && depth >= maxDepth) {
    return { ...base, maxDepth, reason: 'too_deep' }
  }

  return { active: true, depth, time, mix: O2_FLUSH_MIX, reason: null }
}

function resolveO2Flush(settings, segments, alreadyPlanned) {
  const status = describeO2Flush(settings, segments, alreadyPlanned.map(f => f.depth))

  if (status.reason === 'off') return { flush: null, warning: null }

  if (status.reason === 'setting') {
    console.error('buildCCRConfig: the oxygen flush setting is unusable (depth=%o, time=%o)',
      settings.ccrO2FlushDepth, settings.ccrO2FlushTime)
    throw new PlanError(PLAN_ERROR_CODES.INVALID_FLUSH, {
      context: 'buildCCRConfig',
      depth: settings.ccrO2FlushDepth,
      time: settings.ccrO2FlushTime,
      reason: 'o2_flush_setting'
    })
  }

  if (status.reason === 'clash') {
    debugPlanner('buildCCRConfig: the %om oxygen flush of the settings steps aside, a flush is already planned there',
      status.depth)
    return { flush: null, warning: null }
  }

  if (status.reason === 'too_deep') {
    console.error('buildCCRConfig: the %om oxygen flush never happens, the dive never goes below %om',
      status.depth, status.maxDepth)
    return {
      flush: null,
      warning: {
        code: PLAN_WARNING_CODES.O2_FLUSH_TOO_DEEP,
        details: { depth: status.depth, maxDepth: status.maxDepth }
      }
    }
  }

  return {
    flush: { depth: status.depth, time: status.time, keepMix: true, diluent: { ...O2_FLUSH_MIX } },
    warning: null
  }
}

// Build the CCR arguments of a dive: resolve the diluents, attach one to every
// segment, and create the CCR instance the loop calculation runs on.
// Returns { segmentsWithDiluents, diluent, ccrConfig }.
export function buildCCRConfig(adjustedSegments, segments, diluents, ccr, environment, settings = {}) {
  if (!Array.isArray(diluents) || diluents.length === 0) {
    console.error('buildCCRConfig: no diluent available for a rebreather dive (diluents=%o)', diluents)
    throw new PlanError(PLAN_ERROR_CODES.NO_DILUENT, { context: 'buildCCRConfig', diluents })
  }

  const defaultDiluent = diluents.find(d => d.enabled) || diluents[0]

  const mix = source => ({
    name: source.name,
    fO2: source.fO2,
    fHe: source.fHe || 0,
    fN2: 1 - source.fO2 - (source.fHe || 0)
  })

  const segmentsWithDiluents = adjustedSegments.map((seg, idx) => {
    const originalSeg = segments[idx]
    const segDiluent = originalSeg?.diluentIndex !== undefined
      ? diluents[originalSeg.diluentIndex]
      : defaultDiluent
    if (!segDiluent) {
      console.error('buildCCRConfig: segment #%d references diluent #%o, which does not exist among the %d configured',
        idx, originalSeg.diluentIndex, diluents.length)
      throw new PlanError(PLAN_ERROR_CODES.NO_DILUENT, {
        context: 'buildCCRConfig', index: idx, diluentIndex: originalSeg.diluentIndex
      })
    }
    return { ...seg, diluent: mix(segDiluent) }
  })

  const firstSegment = segments[0]
  const segmentDiluent = firstSegment?.diluentIndex !== undefined
    ? diluents[firstSegment.diluentIndex]
    : defaultDiluent
  if (!segmentDiluent) {
    console.error('buildCCRConfig: the first segment references diluent #%o, which does not exist among the %d configured',
      firstSegment.diluentIndex, diluents.length)
    throw new PlanError(PLAN_ERROR_CODES.NO_DILUENT, {
      context: 'buildCCRConfig', index: 0, diluentIndex: firstSegment.diluentIndex
    })
  }
  const diluent = mix(segmentDiluent)
  // `segments` and not `adjustedSegments`: only the depths are read here, and
  // they are the same in both, but the original list is the one the diver wrote.
  const { flushes, warnings: flushWarnings } = resolveFlushes(ccr, diluents, mix, settings, segments)

  // A segment setpoint override forces fixed mode; otherwise the configured
  // mode applies. The environment is passed at construction so the loop mix,
  // the END and the MOD of the diluent are computed at the right altitude and
  // water type before planCCR pushes its own environment.
  const hasOverride = firstSegment?.setpoint !== undefined
  if (hasOverride) {
    const setpoint = firstSegment.setpoint
    return {
      segmentsWithDiluents,
      diluent,
      flushes,
      flushWarnings,
      ccrConfig: new CCR({
        mode: CCR.MODES.FIXED,
        fixedSetpoint: setpoint,
        descentSetpoint: setpoint,
        bottomSetpoint: setpoint,
        ascentSetpoint: setpoint,
        diluent,
        environment
      })
    }
  }

  if (ccr.mode === CCR.MODES.DYNAMIC) {
    return {
      segmentsWithDiluents,
      diluent,
      flushes,
      flushWarnings,
      ccrConfig: new CCR({
        mode: CCR.MODES.DYNAMIC,
        fixedSetpoint: ccr.fixedSetpoint,
        descentSetpoint: ccr.dynamicSetpoints.descent,
        bottomSetpoint: ccr.dynamicSetpoints.bottom,
        ascentSetpoint: ccr.dynamicSetpoints.ascent,
        diluent,
        environment
      })
    }
  }

  return {
    segmentsWithDiluents,
    diluent,
    flushes,
    flushWarnings,
    ccrConfig: new CCR({
      mode: CCR.MODES.FIXED,
      fixedSetpoint: ccr.fixedSetpoint,
      descentSetpoint: ccr.fixedSetpoint,
      bottomSetpoint: ccr.fixedSetpoint,
      ascentSetpoint: ccr.fixedSetpoint,
      diluent,
      environment
    })
  }
}

// Plan one dive, alone or as part of a series.
//
// shared      { settings, environment, gases, diluents }, the day wide inputs
// dive        { segments, ccr }, this dive
// previousDive the { tissues, surfaceInterval, cns, otu } record of the
//              previous dive of the series, null for the first one
//
// Returns { ccr, bailout } with the same meaning as state.results. Every
// failure path throws a PlanError raised by the engine, exactly as the plan
// screen has always propagated it.
export function planDive({ shared, dive, previousDive = null }) {
  const { settings, environment = {}, gases = [], diluents = [] } = shared
  const segments = dive.segments
  const planner = createPlanner(settings, environment)
  const enabledGases = gases.filter(g => g.enabled)
  const adjustedSegments = adjustSegmentsForDescent(segments, settings)

  if (dive.ccr?.enabled) {
    const context = buildCCRConfig(adjustedSegments, segments, diluents, dive.ccr, environment, settings)

    const ccrPlan = planner.planCCR({
      segments: context.segmentsWithDiluents,
      ccr: context.ccrConfig,
      diluent: context.diluent,
      bailoutGases: enabledGases,
      flushes: context.flushes,
      flushWarnings: context.flushWarnings,
      gfLow: settings.gfLow,
      gfHigh: settings.gfHigh,
      previousDive
    })

    const bailoutPlan = planner.planBailout({
      ccrPlan,
      bailoutGases: enabledGases,
      gfLow: settings.gfLow,
      gfHigh: settings.gfHigh,
      rmvDeco: settings.rmvDeco,
      rmvStress: settings.rmvStress,
      stressUntil: settings.stressUntil,
      // `??` and not `||`: a bail delay of 0 min is a setting the diver made,
      // and `|| 2` planned it as two minutes at the bottom. The default only
      // covers a setting that was never written.
      bailDelay: settings.bailDelay ?? 2
    })

    // The diluent actually used travels with the plan: the views are rebuilt on
    // every navigation while the plan lives in the store.
    ccrPlan.diluent = context.diluent

    return { ccr: ccrPlan, bailout: bailoutPlan }
  }

  const ocPlan = planner.planOC({
    segments: resolveSegmentGases(adjustedSegments, enabledGases, 'planDive', gases),
    gases: enabledGases,
    gfLow: settings.gfLow,
    gfHigh: settings.gfHigh,
    rmvBottom: settings.rmvBottom,
    rmvDeco: settings.rmvDeco,
    previousDive
  })

  return { ccr: null, bailout: ocPlan }
}

// The plan a dive of the series is chained from.
//
// On a rebreather dive that is the CCR plan and never the bailout: the bailout
// is the branch where the loop is lost, a dive that did not happen. Chaining
// from it would make the diver pay twice for an incident.
export function chainedPlan(results) {
  if (!results) return null
  return results.ccr || results.bailout || null
}

// Record handed to the engine as previousDive, built from the plan the previous
// dive of the series ended on.
//
// Returns null when there is nothing to chain from, which the engine reads as a
// first dive of the day. A plan without a tissue state is not one this engine
// produced: it is refused here rather than silently planned on clean tissues,
// which would be a repetitive dive with less decompression than the diver owes.
export function previousDiveRecord(results, surfaceInterval, context = 'previousDiveRecord') {
  const plan = chainedPlan(results)
  if (!plan) return null

  if (!plan.tissuesAtSurface) {
    console.error('%s: the previous dive carries no tissue state, it cannot be chained from', context)
    throw new PlanError(PLAN_ERROR_CODES.INVALID_TISSUE_STATE, {
      context, reason: 'missing_tissues_at_surface'
    })
  }

  return {
    tissues: plan.tissuesAtSurface,
    surfaceInterval,
    // Totals and not the exposure of that dive alone: the series accumulates,
    // and dive three inherits what dives one and two left behind
    cns: plan.cnsTotal ?? plan.cns ?? 0,
    otu: plan.otuTotal ?? plan.otu ?? 0,
    // Same reasoning for the DAN flying case: a trimix dive in the morning makes
    // the whole day a day of diving involving helium, whatever the afternoon
    // dive is breathing
    helium: plan.heliumSeries === true
  }
}

// Plan a whole day, chaining every dive to the previous one.
//
// Returns one entry per dive, in order:
//   { index, results, error }
// with results filled and error null when the dive was planned, and the reverse
// when the engine refused it. A refused dive stops the chain - the dives after
// it have no tissue state to start from - so they are reported with the error
// that stopped the series rather than planned on clean tissues.
//
// `from` restarts the day at a given dive, reusing the results already computed
// for the ones before it (see Store.invalidateDayFrom): editing the second dive
// of a day must not recompute the first.
export function planDay({ shared, dives, from = 0 }) {
  if (!Array.isArray(dives)) {
    console.error('planDay: the day carries no dive list (dives=%o)', dives)
    throw new PlanError(PLAN_ERROR_CODES.NO_SEGMENTS, { context: 'planDay', dives })
  }

  const planned = []
  let previousResults = null

  for (let index = 0; index < dives.length; index++) {
    const dive = dives[index]

    // Dives before `from` keep the results they already carry. They are still
    // walked, because the next dive is chained from the last of them.
    if (index < from) {
      if (!dive.results) {
        console.error('planDay: dive #%d was expected to carry its results (from=%d), the whole day is recomputed instead',
          index, from)
        return planDay({ shared, dives, from: 0 })
      }
      planned.push({ index, results: dive.results, error: null })
      previousResults = dive.results
      continue
    }

    if (index > 0 && !previousResults) {
      const reason = 'the previous dive of the series could not be planned'
      console.error('planDay: dive #%d not planned, %s', index, reason)
      planned.push({ index, results: null, error: new PlanError(PLAN_ERROR_CODES.INVALID_TISSUE_STATE, {
        context: 'planDay', index, reason: 'previous_dive_failed'
      }) })
      continue
    }

    try {
      const previousDive = index === 0
        ? null
        : previousDiveRecord(previousResults, dive.surfaceIntervalBefore, `planDay dive #${index}`)

      const results = planDive({ shared, dive, previousDive })
      planned.push({ index, results, error: null })
      previousResults = results

      const plan = chainedPlan(results)
      debugPlanner('planDay: dive #%d planned, runtime %o min, CNS total %o%%',
        index, plan.runtime, plan.cnsTotal)
    } catch (error) {
      if (error instanceof PlanError) {
        console.error('planDay: dive #%d refused by the engine (%s) %o', index, error.code, error.details)
      } else {
        console.error('planDay: dive #%d failed unexpectedly: %s', index, error.message)
      }
      planned.push({ index, results: null, error })
      previousResults = null
    }
  }

  return planned
}
